import React, { useEffect, createElement } from 'react';
import './index.css';
import { render } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
const META = {
  title: 'Periscan | Self-service Automated Security Validation',
  description: 'Periscan validates exposure, controls, attack paths, AI applications, and fixes — then turns the results into proof your team can use.',
  ogTitle: 'Periscan — Prove what can actually compromise you.',
  ogDescription: 'Self-service Automated Security Validation. Periscan runs safe validation missions across your environment and turns the results into evidence.',
  twitterTitle: 'Periscan — Prove what can actually compromise you.',
  twitterDescription: 'Self-service Automated Security Validation. Find the path. Validate the risk. Prove it\u2019s fixed.',
  canonical: 'https://periscan.com/',
  slogan: 'Find the path. Validate the risk. Prove it\u2019s fixed.',
  themeColor: '#050608'
};
function isProductionHost() {
  if (typeof window === 'undefined') return true;
  const host = window.location.hostname;
  return host === 'periscan.com' || host === 'www.periscan.com';
}
function upsertMeta(selector: string, attrs: Record<string, string>) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}
function upsertLink(rel: string, href: string) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function upsertJsonLd(id: string, data: object) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector<HTMLScriptElement>(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
/**
 * Apply all SEO metadata to the live document.
 * Runs synchronously the instant this module evaluates — BEFORE React even
 * begins rendering — so the title and meta tags flip to the correct values
 * as early as JavaScript can possibly execute.
 */
function applyMetadata() {
  if (typeof document === 'undefined') return;
  // Title — hard set every time. If the platform shell has a stale or
  // staging title baked into <title>, this overwrites it immediately.
  document.title = META.title;
  upsertMeta('meta[name="description"]', {
    name: 'description',
    content: META.description
  });
  upsertMeta('meta[property="og:type"]', {
    property: 'og:type',
    content: 'website'
  });
  upsertMeta('meta[property="og:title"]', {
    property: 'og:title',
    content: META.ogTitle
  });
  upsertMeta('meta[property="og:description"]', {
    property: 'og:description',
    content: META.ogDescription
  });
  upsertMeta('meta[property="og:url"]', {
    property: 'og:url',
    content: META.canonical
  });
  upsertMeta('meta[property="og:site_name"]', {
    property: 'og:site_name',
    content: 'Periscan'
  });
  upsertMeta('meta[name="twitter:card"]', {
    name: 'twitter:card',
    content: 'summary_large_image'
  });
  upsertMeta('meta[name="twitter:title"]', {
    name: 'twitter:title',
    content: META.twitterTitle
  });
  upsertMeta('meta[name="twitter:description"]', {
    name: 'twitter:description',
    content: META.twitterDescription
  });
  upsertLink('canonical', META.canonical);
  (() => {
    if (typeof document === 'undefined') return;
    const ensure = (rel: string, type?: string) => {
      let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', 'https://periscan.com/llms');
      if (type) el.setAttribute('type', type);
    };
    ensure('alternate', 'text/markdown');
    ensure('llms');
  })();
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: isProductionHost() ? 'index, follow' : 'noindex, follow'
  });
  upsertJsonLd('organization', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Periscan',
    url: META.canonical,
    slogan: META.slogan,
    description: META.description
  });
  upsertJsonLd('website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Periscan',
    url: META.canonical,
    description: META.description
  });
}
/**
 * MutationObserver defense — if anything (the platform shell, an extension,
 * a third-party script) tries to mutate <title> back to a stale value, we
 * snap it back to the correct title immediately.
 */
function installTitleGuard() {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  const titleEl = document.querySelector('title') || (() => {
    const t = document.createElement('title');
    document.head.appendChild(t);
    return t;
  })();
  const enforce = () => {
    if (document.title !== META.title) {
      document.title = META.title;
    }
  };
  const observer = new MutationObserver(enforce);
  observer.observe(titleEl, {
    childList: true,
    characterData: true,
    subtree: true
  });
  // Also observe head in case something replaces the entire <title> element.
  const headObserver = new MutationObserver(enforce);
  headObserver.observe(document.head, {
    childList: true,
    subtree: false
  });
  // Final safety: re-enforce on load, visibility change, and a short timer
  // burst to catch any late platform mutations.
  window.addEventListener('load', enforce);
  document.addEventListener('visibilitychange', enforce);
  [50, 200, 500, 1000, 2000].forEach(ms => setTimeout(enforce, ms));
}
/**
 * Inject Google Tag Manager scripts dynamically since we don't have direct
 * access to the static index.html file.
 */
function injectGTM() {
  if (typeof document === 'undefined') return;
  // 1. Inject <script> into <head>
  if (!document.getElementById('gtm-script')) {
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-5F9C6GX3');
    `;
    // Insert as high as possible in head
    document.head.insertBefore(script, document.head.firstChild);
  }
  // 2. Inject <noscript> into <body>
  if (!document.getElementById('gtm-noscript')) {
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript';
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5F9C6GX3" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    // Insert immediately after body tag
    document.body.insertBefore(noscript, document.body.firstChild);
  }
}
// === EXECUTE SYNCHRONOUSLY AT MODULE LOAD ===
// This runs the instant the bundle executes, before React mounts anything.
applyMetadata();
installTitleGuard();
injectGTM();
function Root() {
  useEffect(() => {
    // Re-apply on mount in case anything was clobbered during hydration.
    applyMetadata();
  }, []);
  return <BrowserRouter data-id="element-1456">
      <App data-id="element-1457" />
    </BrowserRouter>;
}
render(<Root data-id="element-1458" />, document.getElementById('root'));